/**
 * Gallery lightbox — zoom math (#1694). Pure: no React, no DOM.
 *
 * Port of the zoom helpers in the toolkit's assets/js/property-gallery.js
 * (toolkit#823, shipped 3.280.0); test/widgets/gallery-zoom.test.ts re-asserts
 * the toolkit's own cases.
 *
 * A zoom state is { s, x, y }: the photo scaled by s about its own center,
 * then moved by (x, y) px. A screen point P sits over the unscaled photo
 * offset (P - c - t) / s, c being the photo's layout center and t the
 * translate.
 */

export type ZoomState = { s: number; x: number; y: number }
export type Point = { x: number; y: number }
/** The photo's layout size and the surface it is centred in. */
export type ZoomLimits = { w: number; h: number; cw: number; ch: number }

/** The most a photo zooms. */
export const ZOOM_MAX = 4
/** What the header button zooms to from fit. */
export const ZOOM_STEP = 2.5
/** Fraction of an overshoot the rubber band shows. */
export const ZOOM_BAND = 0.3

export const ZOOM_FIT: Readonly<ZoomState> = Object.freeze({ s: 1, x: 0, y: 0 })

/**
 * Scale to s1 keeping the photo point under m0 pinned under m1: the fingers'
 * midpoint at the pinch start and now, so the pinch pans and scales in one
 * motion, or the cursor twice for a wheel step.
 */
export function zoomAnchor(z: ZoomState, s1: number, c: Point, m0: Point, m1: Point): ZoomState {
  const k = s1 / z.s
  return {
    s: s1,
    x: m1.x - c.x - (m0.x - c.x - z.x) * k,
    y: m1.y - c.y - (m0.y - c.y - z.y) * k,
  }
}

/**
 * Pan limits at scale s: half of the scaled photo's overflow past the
 * surface on each axis, zero where it still fits (it stays centred).
 */
export function zoomBounds(s: number, lim: ZoomLimits): Point {
  return {
    x: Math.max(0, (lim.w * s - lim.cw) / 2),
    y: Math.max(0, (lim.h * s - lim.ch) / 2),
  }
}

/**
 * A rest state: scale within [1, max], translate inside the pan limits;
 * anything within a hair of fit is fit.
 */
export function zoomClamp(z: ZoomState, lim: ZoomLimits, max: number): ZoomState {
  const s = Math.min(max, Math.max(1, z.s))
  if (s <= 1.02) return { s: 1, x: 0, y: 0 }
  const b = zoomBounds(s, lim)
  return {
    s,
    x: Math.min(b.x, Math.max(-b.x, z.x)),
    y: Math.min(b.y, Math.max(-b.y, z.y)),
  }
}

/**
 * Rubber band: past a limit only the fraction k of the overshoot shows, so a
 * pinch or pan can run past the edge and settle back.
 */
export function zoomDamp(v: number, lim: number, k: number): number {
  if (v > lim) return lim + (v - lim) * k
  if (v < -lim) return -lim + (v + lim) * k
  return v
}

export function zoomDampScale(s: number, max: number, k: number): number {
  if (s > max) return max + (s - max) * k
  if (s < 1) return 1 - (1 - s) * k
  return s
}

export const isZoomFit = (z: ZoomState): boolean => z.s === 1 && z.x === 0 && z.y === 0

/** The inline transform for a state; '' at fit so the stylesheet's own transform rules stay in charge. */
export function zoomTransform(z: ZoomState): string {
  return isZoomFit(z) ? '' : `translate(${z.x.toFixed(1)}px, ${z.y.toFixed(1)}px) scale(${z.s.toFixed(3)})`
}
